import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/products", label: "Products" },
    { to: "/about", label: "About Us" },
    { to: "/contact", label: "Contact Us" },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    return (
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-border" data-testid="site-header">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link" onClick={() => setOpen(false)}>
                    <img src="/assets/logo.png" alt="Mamta Enterprises logo" className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded-full shrink-0" data-testid="nav-logo" />
                    <div className="leading-none">
                        <span className="font-display font-600 font-semibold uppercase tracking-wide text-base sm:text-lg text-ink block">Mamta Enterprises</span>
                        <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Wholesale Supplier</span>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-8" data-testid="nav-desktop">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `relative font-display uppercase tracking-[0.15em] text-sm transition-colors duration-200 ${
                                    isActive ? "text-brand" : "text-ink hover:text-brand"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {l.label}
                                    {isActive && (
                                        <motion.span layoutId="nav-underline" className="absolute -bottom-2 left-0 right-0 h-[2px] bg-brand" />
                                    )}
                                </>
                            )}
                        </NavLink>
                    ))}
                    <Link
                        to="/contact"
                        data-testid="nav-quote-button"
                        className="group inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-display uppercase tracking-[0.15em] text-sm px-5 py-2.5 transition-colors duration-200"
                    >
                        Get a Quote
                        <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                </nav>

                <button
                    className="md:hidden p-2 text-ink"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        key={location.pathname + "-mobile"}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="md:hidden overflow-hidden border-t border-border bg-white"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="px-5 py-4 flex flex-col">
                            {LINKS.map((l, i) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    onClick={() => setOpen(false)}
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={({ isActive }) =>
                                        `font-display uppercase tracking-[0.15em] text-lg py-3 border-b border-border last:border-0 ${
                                            isActive ? "text-brand" : "text-ink"
                                        }`
                                    }
                                >
                                    <span className="text-xs text-muted-foreground mr-3">0{i + 1}</span>
                                    {l.label}
                                </NavLink>
                            ))}
                            <Link
                                to="/contact"
                                onClick={() => setOpen(false)}
                                data-testid="nav-mobile-quote-button"
                                className="mt-4 inline-flex items-center justify-center gap-2 bg-brand text-white font-display uppercase tracking-[0.15em] text-sm px-5 py-3"
                            >
                                Get a Quote <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
